import axios, { AxiosRequestConfig } from "axios";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { instance } from "../data/httpClient";

/**
 * Returns a wrapper function that catches 404 AxiosErrors and navigates to
 * /not-found. Other axios errors are left to the interceptor. Non-axios errors
 * are rethrown so the global unhandledrejection listener can handle them.
 *
 * Usage:
 *   const navigateOn404 = useNavigateOn404();
 *   await navigateOn404(async () => {
 *     const data = await fetchSomething(id);
 *     setState(data);
 *   });
 */
export function useNavigateOn404() {
  const navigate = useNavigate();

  return useCallback(
    async (fn: () => Promise<void>) => {
      // Suppress the 404 toast for requests made inside fn().
      // Note: this flag is set on instance.defaults, which is shared across all concurrent requests.
      // In the unlikely event that an unrelated request returns a 404 during this window, its toast
      // will also be suppressed. This is an accepted tradeoff since fixing it would require threading
      // a custom config through every httpClient helper.
      const prev = instance.defaults as AxiosRequestConfig;
      const prevSuppress = prev.suppress404Toast;
      prev.suppress404Toast = true;
      try {
        await fn();
      } catch (error: unknown) {
        // other axios errors (non-404) are already handled by the interceptor
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          navigate("/not-found", { replace: true });
        } else if (!axios.isAxiosError(error)) {
          throw error; // let the global unhandledrejection listener handle it
        }
      } finally {
        prev.suppress404Toast = prevSuppress;
      }
    },
    [navigate]
  );
}
